import { createContext, useContext, ReactNode, useCallback } from 'react';
import { ProfileFull, TeamFull, OrganizationFull, Samen } from '@/types/custom';
import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

interface UserContextType {
  profile: ProfileFull | null;
  loadingProfile: boolean;
  refreshProfile: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export function ProfileProvider({ children }: { children: ReactNode }) {
  const contextValue = _useUserProfile();

  return (
    <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
  );
}

export function useProfile() {
  const context = useContext(UserContext);
  if (context === undefined) {
    console.error('useProfile must be used within a ProfileProvider');
    return { profile: null, loadingProfile: false, refreshProfile: () => {} };
  }
  return context;
}

/*
--- fetch deep info about a given user (including profile)
select t.id as team_id, t.avatar_url as team_avatar_url, team.created_at 
organization_id as organization_id,
sector_id as sector_id from profiles p
left join team_user_link on p.id = team_user_link.user_id
left join team t on team_user_link.team_id = team.id
left join organization on team.organization_id = organization.id 
left join sector on sector.id = organization.id
where p.id = '5db00cae-c321-4bd8-ab0d-c2ae6d2b510a';

--- simplified query to fetch TeamFull for a given user (excluding profile)
SELECT
t.id AS team_id,
t.avatar_url AS team_avatar_url,
t.name AS team_name,
t.organization_id AS team_organziation_id,
t.created_at AS team_created_at,
t.updated_at AS team_updated_at,

o.id AS organization_id,
o.name AS organziation_name,
o.created_at AS organization_created_at,
o.updated_at AS organization_updated_at,

s.id as sector_id,
s.name as sector_name,
s.created_at as sector_created_at,
s.updated_at as sector_updated_at

FROM profiles p
LEFT JOIN team_user_link ON p.id = team_user_link.user_id
LEFT JOIN team t ON t.id = team_user_link.team_id
LEFT JOIN organization o ON t.organization_id = o.id 
LEFT JOIN sector s ON s.id = o.sector_id
where p.id = '5db00cae-c321-4bd8-ab0d-c2ae6d2b510a';
*/

// TODO: merge with useProfile above?
function _useUserProfile(): UserContextType {
  const [profile, setProfile] = useState<ProfileFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshCount, setRefreshCount] = useState<number>(0); // triggers reload of profile

  const refreshProfile = useCallback(() => {
    setRefreshCount((prev) => prev + 1);
  }, []);

  useEffect(() => {
    const getProfile = async () => {
      setLoading(true);
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) {
          return;
        }

        // fetch user profile
        const profilePromise = supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        const rolePromise = supabase
          .from('profile_role')
          .select('role')
          .eq('user_id', user.id)
          .limit(1);

        // https://supabase.com/docs/guides/database/joins-and-nesting
        const teamPromise = supabase
          .from('team_user_link')
          .select(
            `
        team:team_id (
          *,
          organization:organization_id (
            *,
            sector:sector_id (
              *
            )
          )
        )
        `,
          )
          .eq('user_id', user.id) as unknown as {
          // this query above *should* be consistent with TeamFull[]
          data: { team: TeamFull }[] | null;
          error: any;
        };

        const [
          { data: profileData, error: profileError },
          { data: teamData, error: teamError },
          { data: roleData, error: roleError },
        ] = await Promise.all([profilePromise, teamPromise, rolePromise]);

        if (profileError) {
          console.error('Error fetching profile:', profileError);
          setLoading(false);
          return;
        }
        if (teamError) {
          console.error('Error fetching team links:', teamError);
          setLoading(false);
          return;
        }
        if (roleError) {
          console.error('Error fetching role:', roleError);
          setLoading(false);
          return;
        }

        const teams: TeamFull[] = teamData.map((x) => x.team);
        const role = roleData.at(0)?.role;

        // Fetch samen group for hoofdhartuser
        let samen: Samen | null = null;
        if (role === 'hoofdhartuser') {
          // First get the samen_id from the link table
          const { data: samenLink, error: samenLinkError } = await supabase
            .from('samen_user_link')
            .select('samen_id')
            .eq('user_id', user.id)
            .limit(1)
            .single();

          if (samenLinkError) {
            console.error('Error fetching samen link:', samenLinkError);
          }

          if (samenLink?.samen_id) {
            // Then fetch the samen group details
            const { data: samenData, error: samenError } = await supabase
              .from('samen')
              .select('*')
              .eq('id', samenLink.samen_id)
              .single();

            if (samenError) {
              console.error('Error fetching samen:', samenError);
            }

            if (samenData) {
              samen = samenData as Samen;
            }
          }
        }

        const profileWithTeams: ProfileFull = {
          ...profileData,
          role,
          teams,
          samen,
          active_team_idx: 0,
        };

        setProfile(profileWithTeams);
      } catch (error) {
        console.error('Error in useUserProfile:', error);
      } finally {
        setLoading(false);
      }
    };

    getProfile();
  }, [refreshCount]);

  return { profile, loadingProfile: loading, refreshProfile };
}
